import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { uid, money } from '@/lib/helpers';
import {
  MessageCircle, Loader2, Store, Trash2, CheckCircle2, AlertTriangle, XCircle,
  ArrowLeftRight, PackageSearch, Info, ChevronRight, Sparkles, Upload, X, Image as ImageIcon, Calendar, RefreshCw,
  ZoomIn, ZoomOut, Eye, ChevronLeft, History, Archive, Settings as SettingsIcon, Receipt,
} from 'lucide-react';

const WA_SESSION_LS_KEY = 'shabz_wa_import_current';   // instant-cache pointer to the active session

const PAYMENT_METHODS = ['CASH', 'UPI', 'CARD', 'BANK', 'CREDIT', 'UNSPECIFIED'];

const RES = {
  FOUND: { label: 'Found', cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30', icon: CheckCircle2 },
  OTHER_STORE: { label: 'Other store — auto transfer', cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30', icon: ArrowLeftRight },
  UNKNOWN_CODE: { label: 'Unknown — placeholder', cls: 'bg-sky-500/15 text-sky-600 border-sky-500/30', icon: PackageSearch },
  SOLD: { label: 'Previously sold — replacement auto-created', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30', icon: PackageSearch },
  IN_TRANSIT: { label: 'In transit — replacement auto-created', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30', icon: PackageSearch },
  NO_TAG: { label: 'No tag — auto-created', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30', icon: PackageSearch },
  AUTO: { label: 'Auto-created — Needs Review', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30', icon: PackageSearch },
  AMBIGUOUS: { label: 'Auto-created — Needs Review', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30', icon: PackageSearch },
};

const SAMPLE = `33000(1 PK 197J,1 197H,1 HS66H,1 NR07C, 32.5 gpay sz, Cash 500/ Abhi)
20000(3PK39B,40A,198A;2RM967A,1078C;1BH42G; gpay CNR / muskan)`;

// ---------------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH for a draft's live status. Everything (header counters,
// Confirm-All count, per-card badge) is derived from THIS, computed from the
// draft's CURRENT state — never from a cached initial parser/summary snapshot.
//   CONFIRMED / SKIPPED : explicit local lifecycle states
//   BLOCKED             : has a blocking flag (financial ambiguity only)
//   READY               : can_confirm AND a salesperson is selected
//   REVIEW              : everything else (needs a fix before it can confirm)
const draftStatus = (d) => {
  if (!d) return 'REVIEW';
  if (d._state === 'committed') return 'CONFIRMED';
  if (d._state === 'skipped') return 'SKIPPED';
  if ((d.flags || []).some((f) => f.severity === 'block')) return 'BLOCKED';
  if (!d.salesperson || (d.flags || []).some((f) => f.severity === 'warn')) return 'REVIEW';
  return d.can_confirm ? 'READY' : 'REVIEW';
};

const STATUS_BADGE = {
  READY: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  REVIEW: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  BLOCKED: 'bg-rose-500/15 text-rose-600 border-rose-500/30',
  CONFIRMED: 'bg-emerald-600/15 text-emerald-700 border-emerald-600/30',
  SKIPPED: 'bg-muted/60 text-muted-foreground border-border',
};

export default function PasteWhatsappSales() {
  const { currentStore, stores, settings, user } = useApp();
  const navigate = useNavigate();
  const canEditPrice = !!user && (user.role === 'OWNER' || user.role === 'MANAGER'
    || (user.permissions || []).includes('billing_edit'));
  const [addItemFor, setAddItemFor] = useState(null);   // draft index for Add Item dialog
  const [unlistedFor, setUnlistedFor] = useState(null); // draft index for Add Unlisted dialog
  const [storeId, setStoreId] = useState(currentStore || 'store-1');
  const [text, setText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState({});           // index -> bool
  const [dup, setDup] = useState(null);            // { index, invoice_no, minutes }
  const [canManageAliases, setCanManageAliases] = useState(false);
  const [mode, setMode] = useState('text');          // 'text' | 'image'
  const [images, setImages] = useState([]);          // { name, b64 }
  const [saleDate, setSaleDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [importMode, setImportMode] = useState('all');
  const [summary, setSummary] = useState(null);
  const [others, setOthers] = useState([]);
  const [extractStage, setExtractStage] = useState('');
  const [failedShots, setFailedShots] = useState([]);   // [{ index, message }]
  const [shotIdx, setShotIdx] = useState(null);          // open screenshot lightbox at this image index
  const [confirmingAll, setConfirmingAll] = useState(false);
  const committingRef = useRef(new Set());               // indices with an in-flight commit (double-click guard)
  const fileRef = React.useRef(null);
  // --- Phase 3: import session autosave & recovery ---
  const [recentSessions, setRecentSessions] = useState([]);
  const [resumeCandidate, setResumeCandidate] = useState(null);  // unfinished session summary
  const [resuming, setResuming] = useState(false);
  const autosaveTimer = useRef(null);
  const hydratedRef = useRef(false);        // becomes true once a session is active (parse/extract/resume)
  const shotsSavedRef = useRef(new Set());  // session ids whose screenshots are already persisted

  // Live, derived status for every draft — the ONLY source counters read from.
  const statuses = useMemo(() => drafts.map(draftStatus), [drafts]);
  const counts = useMemo(() => {
    const c = { detected: drafts.length, ready: 0, review: 0, blocked: 0, confirmed: 0, skipped: 0 };
    statuses.forEach((s) => { const k = s.toLowerCase(); if (k in c) c[k] += 1; });
    return c;
  }, [drafts.length, statuses]);

  // Back: prefer real history (returns to Billing when that's where we came from),
  // fall back to Billing when there is no usable history. Import session is kept.
  const handleBack = useCallback(() => {
    if (typeof window !== 'undefined' && window.history && window.history.length > 1) navigate(-1);
    else navigate('/billing');
  }, [navigate]);

  const addFiles = useCallback(async (files) => {
    const arr = [];
    for (const f of Array.from(files || [])) {
      if (!f.type || !f.type.startsWith('image/')) continue;
      // eslint-disable-next-line no-await-in-loop
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      arr.push({ name: f.name || 'screenshot', b64 });
    }
    if (arr.length) setImages((cur) => [...cur, ...arr].slice(0, 15));
  }, []);

  const onPasteImage = useCallback((e) => {
    const items = (e.clipboardData && e.clipboardData.items) || [];
    const files = [];
    for (const it of items) { if (it.type && it.type.startsWith('image/')) { const f = it.getAsFile(); if (f) files.push(f); } }
    if (files.length) { e.preventDefault(); addFiles(files); }
  }, [addFiles]);

  const extractShots = useCallback(async (onlyIndices = null) => {
    if (parsing) return;                     // prevent double-click / repeat submit
    const isPartial = Array.isArray(onlyIndices) && onlyIndices.length > 0;
    const targetIdx = isPartial ? onlyIndices : images.map((_, i) => i);
    const payloadImages = targetIdx.map((i) => images[i]?.b64).filter(Boolean);
    if (!payloadImages.length) { toast.error('Add at least one WhatsApp screenshot first.'); return; }

    setParsing(true);
    // Staged progress while the (legitimately long) vision call runs.
    const STAGES = ['Reading screenshot…', 'Extracting WhatsApp messages…', 'Parsing sales…', 'Matching inventory…'];
    let step = 0;
    setExtractStage(STAGES[0]);
    const stageTimer = setInterval(() => {
      step = Math.min(step + 1, STAGES.length - 1);
      setExtractStage(STAGES[step]);
    }, 2500);

    try {
      // Vision is an AI operation: give it up to 90s. This per-request override does
      // NOT change the global 15s API timeout used everywhere else.
      const r = await api.post('/sales/screenshot-extract', {
        store_id: storeId, sale_date: saleDate || null, import_mode: importMode,
        images: payloadImages,
      }, { timeout: 90000 });
      const data = r.data.data;
      // Stable import session identity: keep the current one on a partial retry so
      // autosave/recovery and per-draft confirmation keys stay consistent.
      let sid = session;
      if (!isPartial || !sid) { sid = uid(); setSession(sid); }
      hydratedRef.current = true;
      setStaff(data.staff || []);
      setCanManageAliases(!!data.can_manage_aliases);
      // Map backend per-image errors back to the ORIGINAL image indices.
      const errs = (data.extract_errors || []).map((e) => ({
        index: targetIdx[e.index] != null ? targetIdx[e.index] : e.index,
        message: e.message || "Couldn't read this screenshot.",
      }));
      const newDrafts = (data.drafts || []).map((d) => ({ ...d, _state: 'pending', _original_total: d.total }));

      if (isPartial) {
        // Keep already-successful screenshots; append the retried results.
        setDrafts((cur) => [...cur, ...newDrafts]);
        setOthers((cur) => [...cur, ...(data.others || [])]);
        setSummary((cur) => ({ ...(cur || {}), sales: ((cur && cur.sales) || 0) + newDrafts.length }));
        const retried = new Set(onlyIndices);
        setFailedShots((cur) => [...(cur || []).filter((f) => !retried.has(f.index)), ...errs]);
      } else {
        setSummary(data.summary);
        setOthers(data.others || []);
        setDrafts(newDrafts);
        setFailedShots(errs);
      }

      if (errs.length) {
        toast.warning(`${errs.length} screenshot${errs.length > 1 ? 's' : ''} couldn't be read — retry just those.`);
      } else if (!newDrafts.length && !isPartial) {
        toast.message('No sale messages detected in those screenshots.');
      }
      // Persist screenshots once so they remain viewable after a refresh/resume.
      saveShots(sid, images);
    } catch (e) {
      const timedOut = e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '');
      if (timedOut) {
        // eslint-disable-next-line no-console
        console.warn('[screenshot-extract] client timeout:', e?.message);
        toast.error('Screenshot is taking longer than expected. Please tap Read screenshots to retry.');
      } else {
        toast.error(errMsg(e));
      }
    } finally {
      clearInterval(stageTimer);
      setExtractStage('');
      setParsing(false);
    }
  }, [parsing, images, storeId, saleDate, importMode, session]);   // eslint-disable-line react-hooks/exhaustive-deps

  const sellableStores = (stores || []).filter((s) => s.id && s.name);

  const parse = useCallback(async () => {
    if (!text.trim()) { toast.error('Paste one or more WhatsApp sale messages first.'); return; }
    setParsing(true);
    try {
      const r = await api.post('/sales/parse-whatsapp', { store_id: storeId, text });
      const sid = uid();
      setSession(sid);
      hydratedRef.current = true;
      setResumeCandidate(null);
      setStaff(r.data.data.staff || []);
      setCanManageAliases(!!r.data.data.can_manage_aliases);
      setDrafts((r.data.data.drafts || []).map((d) => ({ ...d, _state: 'pending', _original_total: d.total })));
      if (!r.data.data.drafts?.length) toast.message('No sale messages detected in that text.');
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setParsing(false);
    }
  }, [text, storeId]);

  const revalidate = useCallback(async (index, nextDraft) => {
    try {
      const r = await api.post('/sales/whatsapp-revalidate', { store_id: storeId, draft: nextDraft });
      setDrafts((cur) => cur.map((d, i) => {
        if (i !== index) return d;
        const fresh = r.data.data.draft;
        // Re-apply the user's invoice-level price/discount edits onto the freshly
        // revalidated items (matched by barcode) so manual pricing survives recompute.
        const priced = (fresh.items || []).map((ni) => {
          const prev = (nextDraft.items || []).find((pi) => pi.barcode && pi.barcode === ni.barcode);
          if (prev && (prev.price != null || prev.discount)) {
            return { ...ni, price: prev.price, discount: prev.discount };
          }
          return ni;
        });
        return { ...fresh, items: priced, _state: d._state,
                 _original_total: d._original_total, total: (nextDraft.total ?? fresh.total) };
      }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [storeId]);

  const patchDraft = (index, patch) => {
    setDrafts((cur) => cur.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const editItem = (index, itemIdx, patch) => {
    setDrafts((cur) => cur.map((d, i) => {
      if (i !== index) return d;
      const items = d.items.map((it, j) => (j === itemIdx ? { ...it, ...patch } : it));
      return { ...d, items };
    }));
  };

  const removeItem = (index, itemIdx) => {
    const d = drafts[index];
    const items = d.items.filter((_, j) => j !== itemIdx);
    revalidate(index, { ...d, items });
  };

  // Accept a nearest-AVAILABLE suggestion: replace the item with the real garment,
  // then run the NORMAL revalidation (which clears the SOLD/UNKNOWN flag and, if the
  // match is in the other store, sets up the existing auto-transfer on confirm).
  const acceptSuggestion = useCallback((index, itemIdx, barcode) => {
    setDrafts((cur) => {
      const d = cur[index];
      if (!d) return cur;
      const items = d.items.map((it, j) => (j === itemIdx
        ? { ...it, barcode, is_placeholder: false, derivation: 'explicit',
            suggestions: [], suggestion_confidence: 'none' }
        : it));
      revalidate(index, { ...d, items });
      return cur;
    });
  }, [revalidate]);

  const editPayment = (index, payIdx, patch) => {
    setDrafts((cur) => cur.map((d, i) => {
      if (i !== index) return d;
      const payments = d.payments.map((p, j) => (j === payIdx ? { ...p, ...patch } : p));
      return { ...d, payments };
    }));
  };

  const addPayment = (index) => {
    setDrafts((cur) => cur.map((d, i) => (i === index
      ? { ...d, payments: [...(d.payments || []), { method: 'CASH', account: '', amount: '' }] } : d)));
  };
  const removePayment = (index, payIdx) => {
    setDrafts((cur) => cur.map((d, i) => (i === index
      ? { ...d, payments: (d.payments || []).filter((_, j) => j !== payIdx) } : d)));
  };

  // Directly set the FINAL invoice total (authoritative). The original WhatsApp
  // total is preserved separately (_original_total) for the audit trail.
  const setFinalTotal = (index, value) => {
    const v = value === '' ? '' : Number(value);
    setDrafts((cur) => cur.map((d, i) => (i === index ? { ...d, total: v } : d)));
  };

  // Add an existing garment (searched across BOTH stores). Other-store AVAILABLE
  // items ride the existing auto-transfer on confirm. No stock is touched now.
  const addItemGarment = (index, g) => {
    setDrafts((cur) => {
      const d = cur[index];
      if (!d) return cur;
      const items = [...(d.items || []), { barcode: g.barcode, is_placeholder: false, derivation: 'manual_add' }];
      revalidate(index, { ...d, items });
      return cur;
    });
  };

  // Add an off-tag / unlisted item -> becomes a unique AUTO-WA record only on Confirm.
  const addUnlistedItem = (index, desc, price) => {
    setDrafts((cur) => {
      const d = cur[index];
      if (!d) return cur;
      const items = [...(d.items || []), {
        auto_placeholder: true, reason: 'MANUAL_UNLISTED',
        item_description: (desc || 'Unlisted item').trim(),
        original_whatsapp_text: (desc || '').trim(),
        resolution: 'AUTO', derivation: 'manual_add',
        ...(price ? { price: Number(price) } : {}),
      }];
      revalidate(index, { ...d, items });
      return cur;
    });
  };

  const doCommit = useCallback(async (index, acknowledge) => {
    const d = drafts[index];
    if (!d) return null;
    // Hard double-submit guard: a second click / Confirm-All overlap for the SAME
    // draft must never fire a second request. Pairs with the backend idempotency
    // key so even a race that slips through creates exactly one invoice.
    if (committingRef.current.has(index) || d._state === 'committed') return null;
    committingRef.current.add(index);
    setBusy((b) => ({ ...b, [index]: true }));
    try {
      const r = await api.post('/sales/whatsapp-commit', {
        store_id: storeId,
        // Stable per-sale import identity: session + position + message fingerprint.
        // Retries/double-clicks reuse it (idempotent); genuinely different sales get
        // a different key, so identical-looking sales are never wrongly merged.
        client_txn_id: `${session}:${index}:${d.fingerprint || 'nofp'}`,
        acknowledge_duplicate: !!acknowledge,
        sale_date: saleDate || (d.source && d.source.sale_date) || null,
        draft: {
          total: d.total, original_total: (d._original_total != null ? d._original_total : d.total),
          fingerprint: d.fingerprint, raw_text: d.raw_text,
          salesperson: d.salesperson, source: d.source,
          items: d.items.map((it) => (
            it.auto_placeholder ? {
              auto_placeholder: true, reason: it.reason || 'AUTO_PLACEHOLDER',
              item_description: it.item_description, original_whatsapp_code: it.original_whatsapp_code,
              original_whatsapp_text: it.original_whatsapp_text, exclude: !!it.exclude,
            } : it.untagged ? {
              untagged: true, reason: it.reason || 'NO_TAG',
              item_description: it.item_description, original_whatsapp_text: it.original_whatsapp_text,
              exclude: !!it.exclude,
            } : {
              barcode: it.barcode, is_placeholder: !!it.is_placeholder, exclude: !!it.exclude,
              ...(it.price != null && it.price !== '' ? { price: Number(it.price) } : {}),
              ...(it.discount ? { discount: Number(it.discount) } : {}),
            })),
          payments: d.payments.map((p) => ({ method: p.method, account: p.account, amount: (p.amount === '' ? null : p.amount) })),
          aliases_used: d.aliases_used || [],
        },
      });
      const data = r.data.data;
      if (data.status === 'duplicate_warning') {
        setDup({ index, ...data });
        return null;
      }
      patchDraft(index, { _state: 'committed', _result: data });
      if (data.idempotent_replay) toast.message(`Already recorded · invoice ${data.invoice_no}`);
      else toast.success(`Invoice ${data.invoice_no} created · ${money(data.net)}`);
      return data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    } finally {
      committingRef.current.delete(index);
      setBusy((b) => ({ ...b, [index]: false }));
    }
  }, [drafts, storeId, session, saleDate]);

  const storeName = (id) => (stores || []).find((s) => s.id === id)?.name || id;

  const saveAlias = useCallback(async (alias, name) => {
    if (!alias || !name) return;
    try {
      await api.post('/wa-alias-rules', { alias_text: alias, normalized_value: name, alias_type: 'SALESPERSON' });
      toast.success(`Remembered: "${alias}" → ${name}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  const pending = drafts.filter((d) => d._state === 'pending');

  const confirmAllReady = useCallback(async () => {
    if (confirmingAll) return;
    // Operate ONLY on drafts currently classified READY (live state). Review/Blocked
    // drafts never block the ready ones; already-confirming/confirmed are skipped.
    const ready = drafts.map((d, i) => ({ d, i }))
      .filter(({ d, i }) => draftStatus(d) === 'READY' && !committingRef.current.has(i));
    if (!ready.length) { toast.error('No ready sales to confirm.'); return; }
    setConfirmingAll(true);
    try {
      for (const { i } of ready) {
        // eslint-disable-next-line no-await-in-loop
        await doCommit(i, false);
      }
    } finally {
      setConfirmingAll(false);
    }
  }, [drafts, doCommit, confirmingAll]);

  // ======================================================================
  // PHASE 3 — IMPORT SESSION AUTOSAVE & RECOVERY
  // A full snapshot of the review session is debounced to the backend (source
  // of truth) with an instant localStorage pointer, so a refresh / crash / Back
  // / navigation never loses reviewed work. Confirmed drafts keep _state so they
  // restore as CONFIRMED and (with backend idempotency) can never re-bill.
  // ======================================================================
  const buildSnapshot = useCallback(() => ({
    id: session,
    store_id: storeId,
    store_name: storeName(storeId),
    sale_date: saleDate || null,
    input_method: mode,
    raw_text: text,
    drafts,
    others,
    summary,
    staff,
    has_shots: mode === 'image' && images.length > 0,
  }), [session, storeId, saleDate, mode, text, drafts, others, summary, staff, images.length]);

  const persistSession = useCallback(async () => {
    if (!session) return;
    if (!drafts.length && !text.trim()) return;
    try {
      const snap = buildSnapshot();
      await api.post('/import-sessions/save', snap);
      try { localStorage.setItem(WA_SESSION_LS_KEY, JSON.stringify({ id: session, ts: Date.now() })); } catch (e) { console.debug('[wa-import] localStorage write skipped', e); }
    } catch (e) { console.debug('[wa-import] autosave skipped (best-effort)', e); }
  }, [session, drafts.length, text, buildSnapshot]);

  // Debounced autosave — batches rapid edits (never saves on every keystroke).
  useEffect(() => {
    if (!session || parsing || resuming) return;
    if (!drafts.length) return;
    hydratedRef.current = true;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { persistSession(); }, 1300);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, drafts, text, storeId, saleDate, mode, others, summary, parsing, resuming]);

  const loadRecent = useCallback(async () => {
    try {
      const r = await api.get('/import-sessions');
      const list = r.data.data.sessions || [];
      setRecentSessions(list);
      return list;
    } catch (e) { console.debug('[wa-import] loadRecent failed', e); return []; }
  }, []);

  // On first mount, surface any unfinished session for recovery (B2) — but never
  // auto-destroy or auto-open it. We prefer the localStorage-pointed session.
  useEffect(() => {
    (async () => {
      const list = await loadRecent();
      if (hydratedRef.current || drafts.length) return;
      let pointerId = null;
      try { pointerId = (JSON.parse(localStorage.getItem(WA_SESSION_LS_KEY) || '{}') || {}).id; } catch (e) { console.debug('[wa-import] pointer read skipped', e); }
      const unfinished = list.filter((s) => s.status === 'IN_PROGRESS' && (s.counts?.detected || 0) > 0);
      const pick = unfinished.find((s) => s.id === pointerId) || unfinished[0];
      if (pick) setResumeCandidate(pick);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resumeSession = useCallback(async (sid) => {
    setResuming(true);
    try {
      const r = await api.get(`/import-sessions/${sid}`);
      const s = r.data.data.session;
      setSession(s.id);
      if (s.store_id) setStoreId(s.store_id);
      if (s.sale_date) setSaleDate(s.sale_date);
      setMode(s.input_method || 'text');
      setText(s.raw_text || '');
      setStaff(s.staff || []);
      setDrafts(s.drafts || []);
      setOthers(s.others || []);
      setSummary(s.summary || null);
      setImages((s.images || []).map((im) => ({ name: im.name || 'screenshot', b64: im.b64 })));
      setCanManageAliases(!!user && ['OWNER', 'MANAGER'].includes(user.role));
      setResumeCandidate(null);
      committingRef.current = new Set();
      hydratedRef.current = true;
      if (s.images && s.images.length) shotsSavedRef.current.add(s.id);
      try { localStorage.setItem(WA_SESSION_LS_KEY, JSON.stringify({ id: s.id, ts: Date.now() })); } catch (e) { console.debug('[wa-import] localStorage write skipped', e); }
      toast.success('Import session resumed.');
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setResuming(false);
    }
  }, [user]);

  const startNew = useCallback(() => {
    // Keep the previous session intact (it stays under Recent Imports); just begin fresh.
    setResumeCandidate(null);
    hydratedRef.current = true;
    try { localStorage.removeItem(WA_SESSION_LS_KEY); } catch (e) { console.debug('[wa-import] localStorage clear skipped', e); }
  }, []);

  const archiveSession = useCallback(async (sid) => {
    try {
      await api.post(`/import-sessions/${sid}/archive`);
      setRecentSessions((cur) => cur.filter((s) => s.id !== sid));
      if (resumeCandidate && resumeCandidate.id === sid) setResumeCandidate(null);
      toast.message('Import closed.');
    } catch (e) { toast.error(errMsg(e)); }
  }, [resumeCandidate]);

  // Persist screenshots once per session (kept out of the autosave payload for speed).
  const saveShots = useCallback(async (sid, imgs) => {
    if (!sid || !imgs || !imgs.length) return;
    if (shotsSavedRef.current.has(sid)) return;
    shotsSavedRef.current.add(sid);
    try {
      await api.post('/import-sessions/save-shots', { session_id: sid, images: imgs });
    } catch (e) { console.debug('[wa-import] saveShots failed; will retry', e); shotsSavedRef.current.delete(sid); }
  }, []);

  // "Remember this correction" for a payment account -> learned PAYMENT_ACCOUNT rule.
  const saveAcctAlias = useCallback(async (alias, account) => {
    if (!alias || !account) return;
    try {
      await api.post('/wa-alias-rules', { alias_text: alias, normalized_value: account, alias_type: 'PAYMENT_ACCOUNT' });
      toast.success(`Remembered: "${alias}" → ${account}`);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  return (
    <Page>
      <TopBar title="Paste WhatsApp Sales" onBack={handleBack} />
      <div className="px-4 pt-4 pb-40 max-w-3xl mx-auto">
        {/* Unfinished import recovery (never auto-destroyed) */}
        {resumeCandidate && drafts.length === 0 && (
          <div className="mb-4 rounded-2xl border border-primary/30 bg-primary/5 p-4 shadow-sm" data-testid="wa-resume-banner">
            <div className="flex items-start gap-3">
              <History className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Unfinished import found</p>
                <p className="mt-0.5 text-xs text-muted-foreground" data-testid="wa-resume-summary">
                  {(resumeCandidate.counts?.detected || 0)} sales
                  {resumeCandidate.counts?.ready ? ` · ${resumeCandidate.counts.ready} Ready` : ''}
                  {resumeCandidate.counts?.review ? ` · ${resumeCandidate.counts.review} Review` : ''}
                  {resumeCandidate.counts?.confirmed ? ` · ${resumeCandidate.counts.confirmed} Confirmed` : ''}
                  {resumeCandidate.store_name ? ` · ${resumeCandidate.store_name}` : ''}
                </p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <Button size="sm" data-testid="wa-resume-btn" disabled={resuming}
                    onClick={() => resumeSession(resumeCandidate.id)} className="h-8 rounded-lg text-xs font-semibold">
                    {resuming ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <History className="mr-1.5 h-4 w-4" />}Resume
                  </Button>
                  <Button size="sm" variant="ghost" data-testid="wa-resume-startnew" onClick={startNew}
                    className="h-8 rounded-lg text-xs">Start new</Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Store + input */}
        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid="wa-input-card">
          <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold">
            <MessageCircle className="h-4 w-4 text-emerald-500" /> Import sales from WhatsApp messages
          </p>
          <div className="flex items-center gap-2 mb-3">
            <Store className="h-4 w-4 shrink-0 text-primary" />
            <Label className="shrink-0 text-sm">Billing store</Label>
            <div className="ml-auto w-48 max-w-[60%]">
              <Select value={storeId} onValueChange={setStoreId}>
                <SelectTrigger data-testid="wa-store-select" className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sellableStores.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`wa-store-opt-${s.id}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mb-3 flex items-center gap-2">
            <Calendar className="h-4 w-4 shrink-0 text-primary" />
            <Label className="shrink-0 text-sm">Sale date</Label>
            <div className="ml-auto w-48 max-w-[60%]">
              <Input type="date" data-testid="wa-sale-date" value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)} className="h-10 rounded-xl" />
            </div>
          </div>
          <div className="mb-3 flex gap-1 rounded-xl bg-muted/60 p-1" data-testid="wa-mode-tabs">
            <button data-testid="wa-mode-text" onClick={() => setMode('text')}
              className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${mode === 'text' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}>
              Paste text
            </button>
            <button data-testid="wa-mode-image" onClick={() => setMode('image')}
              className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${mode === 'image' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}>
              Upload screenshots
            </button>
          </div>
          {mode === 'text' ? (
          <Textarea
            data-testid="wa-paste-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Paste one or more messages, e.g.\n${SAMPLE}`}
            className="min-h-[120px] rounded-xl font-mono text-sm"
          />
          ) : (
          <div data-testid="wa-image-panel">
            <div className="mb-3">
              <Label className="text-[11px] text-muted-foreground">Import</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger data-testid="wa-import-mode" className="mt-1 h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All visible sales</SelectItem>
                  <SelectItem value="select">Select messages</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div
              data-testid="wa-dropzone"
              tabIndex={0}
              onPaste={onPasteImage}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              onClick={() => fileRef.current && fileRef.current.click()}
              className="flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted/30 p-4 text-center transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
              <Upload className="mb-1.5 h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium">Drop screenshots, click to browse, or paste (Ctrl+V)</p>
              <p className="text-[11px] text-muted-foreground">WhatsApp sales screenshots · up to 15 · PNG/JPG/WEBP</p>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                data-testid="wa-file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            </div>
            {images.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="wa-image-thumbs">
                {images.map((im, i) => (
                  <div key={i} className="relative" data-testid={`wa-thumb-${i}`}>
                    <img src={im.b64} alt={im.name} className="h-16 w-16 rounded-lg border border-border object-cover" />
                    <button onClick={(e) => { e.stopPropagation(); setImages((cur) => cur.filter((_, j) => j !== i)); }}
                      data-testid={`wa-thumb-remove-${i}`}
                      className="absolute -right-1.5 -top-1.5 rounded-full bg-rose-500 p-0.5 text-white shadow">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            {mode === 'text' ? (
            <Button data-testid="wa-parse-button" onClick={parse} disabled={parsing} className="h-11 rounded-xl font-semibold">
              {parsing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Parse messages
            </Button>
            ) : (
            <Button data-testid="wa-extract-button" onClick={() => extractShots()} disabled={parsing || !images.length} className="h-11 rounded-xl font-semibold">
              {parsing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-2 h-4 w-4" />}
              {parsing ? (extractStage || 'Reading…') : 'Read screenshots'}
            </Button>
            )}
            {mode === 'text' && !text && (
              <Button variant="ghost" data-testid="wa-sample-button" onClick={() => setText(SAMPLE)} className="h-11 rounded-xl text-xs">
                Use a sample
              </Button>
            )}
          </div>
          {mode === 'image' && failedShots.length > 0 && (
            <div data-testid="wa-partial-failures" className="mt-2 rounded-xl border border-amber-500/40 bg-amber-500/5 p-2.5">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                {failedShots.length} screenshot{failedShots.length > 1 ? 's' : ''} couldn&apos;t be read
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Successful screenshots are kept. Retry only the ones that failed.
              </p>
              <Button size="sm" variant="outline" data-testid="wa-retry-failed" disabled={parsing}
                className="mt-2 h-8 rounded-lg" onClick={() => extractShots(failedShots.map((f) => f.index))}>
                {parsing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
                Retry failed
              </Button>
            </div>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <Button variant="ghost" size="sm" data-testid="wa-needs-review-link"
              onClick={() => navigate('/inventory-review')} className="h-8 rounded-lg px-2 text-[11px] text-muted-foreground">
              <PackageSearch className="mr-1.5 h-3.5 w-3.5" /> Review placeholder items (Inventory Review)
            </Button>
            <Button variant="ghost" size="sm" data-testid="wa-import-settings-link"
              onClick={() => navigate('/wa-import-settings')} className="h-8 rounded-lg px-2 text-[11px] text-muted-foreground">
              <SettingsIcon className="mr-1.5 h-3.5 w-3.5" /> Import settings · Learned aliases
            </Button>
          </div>
        </div>

        {/* Recent imports — resume / close earlier sessions (B4) */}
        {drafts.length === 0 && recentSessions.length > 0 && (
          <div className="mt-5 rounded-2xl border border-border bg-card p-3 shadow-sm" data-testid="wa-recent-imports">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <History className="h-3.5 w-3.5" /> Recent imports
            </p>
            <div className="space-y-1.5">
              {recentSessions.slice(0, 8).map((s) => (
                <div key={s.id} data-testid={`wa-recent-${s.id}`}
                  className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-xs">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">
                      {(s.sale_date || (s.updated_at || '').slice(0, 10) || '—')} · {s.store_name || s.store_id || '—'} · {(s.counts?.detected || 0)} sales
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {s.status === 'COMPLETED' ? 'Completed'
                        : s.status === 'IN_PROGRESS'
                          ? `In progress${s.counts?.review ? ` · ${s.counts.review} Review` : ''}${s.counts?.confirmed ? ` · ${s.counts.confirmed} Confirmed` : ''}`
                          : s.status}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`wa-recent-resume-${s.id}`} disabled={resuming}
                    onClick={() => resumeSession(s.id)} className="h-7 shrink-0 rounded-lg text-[11px]">
                    {s.status === 'COMPLETED' ? 'View' : 'Resume'}
                  </Button>
                  <button type="button" data-testid={`wa-recent-archive-${s.id}`} onClick={() => archiveSession(s.id)}
                    className="shrink-0 text-muted-foreground hover:text-rose-500" aria-label="Close import">
                    <Archive className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live import summary — ALWAYS derived from the CURRENT draft state. */}
        {drafts.length > 0 && (
          <div className="mt-5 rounded-2xl border border-border bg-card p-3 shadow-sm" data-testid="wa-summary">
            <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
              <Badge variant="outline" className="bg-muted/60" data-testid="wa-count-detected">{counts.detected} detected</Badge>
              <Badge variant="outline" className={STATUS_BADGE.READY} data-testid="wa-count-ready">{counts.ready} Ready</Badge>
              {counts.review > 0 && <Badge variant="outline" className={STATUS_BADGE.REVIEW} data-testid="wa-count-review">{counts.review} Review</Badge>}
              {counts.blocked > 0 && <Badge variant="outline" className={STATUS_BADGE.BLOCKED} data-testid="wa-count-blocked">{counts.blocked} Blocked</Badge>}
              {counts.confirmed > 0 && <Badge variant="outline" className={STATUS_BADGE.CONFIRMED} data-testid="wa-count-confirmed">{counts.confirmed} Confirmed</Badge>}
              {counts.skipped > 0 && <Badge variant="outline" className={STATUS_BADGE.SKIPPED} data-testid="wa-count-skipped">{counts.skipped} Skipped</Badge>}
              {summary && summary.exchange > 0 && <Badge variant="outline" className="bg-sky-500/15 text-sky-600 border-sky-500/30">{summary.exchange} Exchange</Badge>}
              {summary && summary.credit_note > 0 && <Badge variant="outline" className="bg-violet-500/15 text-violet-600 border-violet-500/30">{summary.credit_note} Credit note</Badge>}
              {summary && summary.ignored > 0 && <Badge variant="outline" className="bg-muted/60 text-muted-foreground">{summary.ignored} Ignored</Badge>}
              <div className="ml-auto flex items-center gap-2">
                {images.length > 0 && (
                  <Button size="sm" variant="outline" data-testid="wa-view-screenshot"
                    onClick={() => setShotIdx(0)} className="h-8 rounded-lg text-xs">
                    <Eye className="mr-1.5 h-4 w-4" />View screenshot{images.length > 1 ? `s (${images.length})` : ''}
                  </Button>
                )}
                {counts.ready > 0 && (
                  <Button size="sm" data-testid="wa-confirm-all-ready" onClick={confirmAllReady} disabled={confirmingAll}
                    className="h-8 rounded-lg text-xs font-semibold">
                    {confirmingAll ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                    {confirmingAll ? 'Confirming…' : `Confirm all ${counts.ready} ready sale${counts.ready === 1 ? '' : 's'}`}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Non-sale messages (exchanges / credit notes / ignored) */}
        {others.length > 0 && (
          <div className="mt-3 rounded-2xl border border-border bg-muted/30 p-3" data-testid="wa-others">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Not imported as sales ({others.length})
            </p>
            <div className="space-y-1">
              {others.map((o, i) => (
                <details key={i} className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs" data-testid={`wa-other-${i}`}>
                  <summary className="flex cursor-pointer items-center gap-2">
                    <Badge variant="outline" className="shrink-0 bg-muted/60 text-[10px]">{o.classification}</Badge>
                    <span className="truncate text-muted-foreground">{(o.text || '').slice(0, 60) || '(no text)'}</span>
                    {o.wa_time ? <span className="ml-auto shrink-0 text-muted-foreground/70">{o.wa_time}</span> : null}
                  </summary>
                  <p className="mt-1.5 whitespace-pre-wrap font-mono text-[11px] text-muted-foreground">{o.text}</p>
                </details>
              ))}
            </div>
          </div>
        )}

        {/* Pending drafts */}
        {pending.length > 0 && (
          <p className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="wa-drafts-heading">
            {pending.length} sale{pending.length === 1 ? '' : 's'} to review
          </p>
        )}
        {drafts.map((d, index) => d._state === 'committed' ? (
          <div key={index} data-testid={`wa-draft-committed-${index}`} className="mt-3 flex items-center gap-2 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <span className="text-sm font-medium">Invoice {d._result?.invoice_no} created · {money(d._result?.net)}</span>
            <Button size="sm" variant="ghost" className="ml-auto" data-testid={`wa-view-invoice-${index}`}
              onClick={() => navigate(`/invoice/${d._result?.invoice_no}`)}>View <ChevronRight className="h-4 w-4" /></Button>
          </div>
        ) : d._state === 'skipped' ? (
          <div key={index} data-testid={`wa-draft-skipped-${index}`} className="mt-3 flex items-center gap-2 rounded-2xl border border-border bg-muted/40 p-4 text-muted-foreground">
            <XCircle className="h-5 w-5" />
            <span className="text-sm">Skipped · {money(d.total)}</span>
            <Button size="sm" variant="ghost" className="ml-auto" data-testid={`wa-unskip-${index}`}
              onClick={() => patchDraft(index, { _state: 'pending' })}>Undo</Button>
          </div>
        ) : (
          <DraftCard
            key={index}
            index={index}
            draft={d}
            status={statuses[index]}
            staff={staff}
            busy={!!busy[index]}
            storeName={storeName}
            onEditItem={editItem}
            onRemoveItem={removeItem}
            onAcceptSuggestion={acceptSuggestion}
            onEditPayment={editPayment}
            onAddPayment={addPayment}
            onRemovePayment={removePayment}
            onAddItem={(i) => setAddItemFor(i)}
            onAddUnlisted={(i) => setUnlistedFor(i)}
            onSetFinalTotal={setFinalTotal}
            canEditPrice={canEditPrice}
            sourceImage={d.source && d.source.screenshot_index != null && images[d.source.screenshot_index]
              ? images[d.source.screenshot_index].b64 : null}
            onPatch={patchDraft}
            onRevalidate={revalidate}
            onCommit={doCommit}
            onViewSource={d.source && d.source.screenshot_index != null && images[d.source.screenshot_index]
              ? () => setShotIdx(d.source.screenshot_index) : null}
            canManageAliases={canManageAliases}
            onSaveAlias={saveAlias}
            onSaveAcctAlias={saveAcctAlias}
          />
        ))}

        {drafts.length > 0 && pending.length === 0 && (
          <Button data-testid="wa-done-button" onClick={() => navigate('/billing')} className="mt-6 h-12 w-full rounded-xl font-semibold">
            Done — back to Billing
          </Button>
        )}
      </div>

      {/* Duplicate acknowledgement */}
      <Dialog open={!!dup} onOpenChange={(o) => !o && setDup(null)}>
        <DialogContent data-testid="wa-duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" />Possible duplicate</DialogTitle>
            <DialogDescription>
              An identical sale was recorded {dup?.minutes} minute{dup?.minutes === 1 ? '' : 's'} ago.
              Review the details, then decide whether to record this sale again.
            </DialogDescription>
          </DialogHeader>
          {dup && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-sm" data-testid="wa-dup-details">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-semibold"><Receipt className="h-4 w-4 text-amber-600" />{dup.invoice_no}</span>
                <span className="font-display text-lg font-bold" data-testid="wa-dup-amount">{money(dup.net)}</span>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>Salesperson</span><span className="text-right font-medium text-foreground" data-testid="wa-dup-salesperson">{dup.salesperson || '—'}</span>
                <span>Sale date</span><span className="text-right font-medium text-foreground">{dup.sale_date || (dup.created_at || '').slice(0, 10) || '—'}</span>
              </div>
              {(dup.items || []).length > 0 && (
                <div className="mt-2 border-t border-amber-500/20 pt-2">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Items</p>
                  <div className="space-y-0.5">
                    {dup.items.map((it, k) => (
                      <div key={k} className="flex items-center justify-between text-xs" data-testid={`wa-dup-item-${k}`}>
                        <span className="truncate font-mono">{it.barcode || it.product || 'item'}</span>
                        <span className="ml-2 shrink-0 text-muted-foreground">{it.price != null ? money(it.price) : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button type="button" data-testid="wa-dup-view-invoice"
                onClick={() => navigate(`/invoice/${dup.invoice_no}`)}
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                <Eye className="h-3.5 w-3.5" />Inspect existing invoice
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="wa-dup-cancel" onClick={() => setDup(null)}>It&apos;s the same — cancel</Button>
            <Button data-testid="wa-dup-confirm" onClick={() => { const i = dup.index; setDup(null); doCommit(i, true); }}>
              This is a separate sale — record it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Original screenshot lightbox (zoom + pan + switch between screenshots) */}
      <ScreenshotLightbox
        images={images}
        index={shotIdx}
        onIndex={setShotIdx}
        onClose={() => setShotIdx(null)}
      />

      <AddItemDialog
        open={addItemFor != null}
        storeId={storeId}
        storeName={storeName}
        onClose={() => setAddItemFor(null)}
        onPick={(g) => { addItemGarment(addItemFor, g); setAddItemFor(null); }}
      />
      <AddUnlistedDialog
        open={unlistedFor != null}
        onClose={() => setUnlistedFor(null)}
        onAdd={(desc, price) => { addUnlistedItem(unlistedFor, desc, price); setUnlistedFor(null); }}
      />
    </Page>
  );
}

function DraftCard({ index, draft: d, status, staff, busy, storeName, onEditItem, onRemoveItem, onAcceptSuggestion, onEditPayment, onAddPayment, onRemovePayment, onAddItem, onAddUnlisted, onSetFinalTotal, canEditPrice, sourceImage, onPatch, onRevalidate, onCommit, onViewSource, canManageAliases, onSaveAlias, onSaveAcctAlias }) {
  const blockFlags = (d.flags || []).filter((f) => f.severity === 'block');
  const warnFlags = (d.flags || []).filter((f) => f.severity === 'warn');
  const canConfirm = d.can_confirm && d.salesperson;
  const st = status || draftStatus(d);
  const [showEdit, setShowEdit] = useState(false);
  const [showSource, setShowSource] = useState(true);
  const [billMode, setBillMode] = useState('AMT'); // 'AMT' | 'PCT'
  const [billInput, setBillInput] = useState('');

  const isPriceable = (it) => (it.resolution === 'FOUND' || it.resolution === 'OTHER_STORE');
  const basePrice = (it) => ((it.price != null && it.price !== '') ? Number(it.price) : Number(it.list_price || 0));
  const lineAmount = (it) => {
    if (!isPriceable(it)) return null; // placeholder amounts are auto-distributed from the total
    const base = (it.price != null && it.price !== '') ? Number(it.price) : Number(it.list_price || 0);
    return Math.max(0, base - Number(it.discount || 0));
  };
  const pricedItems = (d.items || []).filter((it) => !it.exclude && lineAmount(it) != null);
  const unpricedCount = (d.items || []).filter((it) => !it.exclude && lineAmount(it) == null).length;
  const pricedSubtotal = pricedItems.reduce((s, it) => s + lineAmount(it), 0);
  const finalTotal = Number(d.total || 0);
  const billDiscount = (unpricedCount === 0) ? Math.max(0, Math.round((pricedSubtotal - finalTotal) * 100) / 100) : 0;
  const payTotal = (d.payments || []).reduce((s, p) => s + (p.amount === '' || p.amount == null ? 0 : Number(p.amount)), 0);
  const received = (d.payments || []).filter((p) => p.method !== 'CREDIT').reduce((s, p) => s + (p.amount === '' || p.amount == null ? 0 : Number(p.amount)), 0);
  const balanceDue = Math.max(0, Math.round((finalTotal - received) * 100) / 100);

  return (
    <div className="mt-3 rounded-2xl border border-border bg-card shadow-sm overflow-hidden" data-testid={`wa-draft-${index}`}>
      {/* Header: total + raw */}
      <div className="flex items-start justify-between gap-3 bg-muted/40 px-4 py-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">WhatsApp total</p>
          <p className="font-display text-2xl font-bold" data-testid={`wa-total-${index}`}>{money(d.total)}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5" data-testid={`wa-draft-state-${index}`}>
            <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[st] || STATUS_BADGE.REVIEW}`}>{st}</Badge>
            {d.source && d.source.wa_time ? <span className="text-[10px] text-muted-foreground">{d.source.wa_time}</span> : null}
            {d.source && d.source.sale_date ? <span className="text-[10px] text-muted-foreground">· {d.source.sale_date}</span> : null}
            {d.source && d.source.screenshot_index != null ? (
              <span className="text-[10px] text-muted-foreground">· Screenshot {d.source.screenshot_index + 1}</span>
            ) : null}
            {onViewSource && (
              <button type="button" data-testid={`wa-view-source-${index}`} onClick={onViewSource}
                className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted">
                <Eye className="h-3 w-3" /> View source
              </button>
            )}
          </div>
        </div>
        <p className="min-w-0 flex-1 text-right font-mono text-[11px] leading-snug text-muted-foreground break-words" data-testid={`wa-raw-${index}`}>
          {d.raw_text}
        </p>
      </div>

      <div className="p-4 space-y-4">
        {/* Items */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Items</p>
          <div className="space-y-2">
            {d.items.map((it, j) => {
              const r = RES[it.resolution] || RES.AMBIGUOUS;
              const Icon = r.icon;
              return (
                <div key={j} className="rounded-xl border border-border p-2.5" data-testid={`wa-item-${index}-${j}`}>
                  <div className="flex items-center gap-2">
                    {it.untagged || it.auto_placeholder ? (
                      <div className="flex h-9 min-w-32 items-center rounded-lg border border-blue-500/30 bg-blue-500/5 px-3 text-sm font-medium text-blue-700 dark:text-blue-300"
                        data-testid={`wa-item-auto-${index}-${j}`}>
                        {it.item_description || 'Unlabelled item'}{it.untagged ? ' · No Tag' : ''}
                      </div>
                    ) : (
                      <Input
                        value={it.barcode || ''}
                        onChange={(e) => onEditItem(index, j, { barcode: e.target.value.toUpperCase() })}
                        onBlur={() => onRevalidate(index, d)}
                        data-testid={`wa-item-code-${index}-${j}`}
                        className="h-9 w-32 rounded-lg font-mono text-sm"
                        placeholder="CODE"
                      />
                    )}
                    <Badge variant="outline" className={`gap-1 ${r.cls}`} data-testid={`wa-item-status-${index}-${j}`}>
                      <Icon className="h-3 w-3" />{r.label}
                    </Badge>
                    {it.resolution === 'OTHER_STORE' && (
                      <span className="text-[11px] text-muted-foreground">from {it.from_store_name}</span>
                    )}
                    <button onClick={() => onRemoveItem(index, j)} data-testid={`wa-item-remove-${index}-${j}`}
                      className="ml-auto text-muted-foreground hover:text-rose-500" aria-label="Remove item">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] text-muted-foreground" data-testid={`wa-item-explain-${index}-${j}`}>
                      {it.explain}
                      {it.lookup_result ? <span className="ml-1 text-muted-foreground/70">· lookup: {it.lookup_result}</span> : null}
                    </span>
                    {(it.resolution === 'UNKNOWN_CODE' || it.resolution === 'AUTO' || it.untagged) && (
                      <span className="flex items-center gap-1.5 text-[11px] font-medium text-blue-600" data-testid={`wa-item-autoinfo-${index}-${j}`}>
                        <PackageSearch className="h-3.5 w-3.5" /> Inventory record will be auto-created
                      </span>
                    )}
                    {(it.resolution === 'FOUND' || it.resolution === 'OTHER_STORE') && (
                      <span className="text-[11px] text-muted-foreground">List {money(it.list_price)}</span>
                    )}
                  </div>
                  {showEdit && canEditPrice && isPriceable(it) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-muted/40 p-2" data-testid={`wa-item-pricing-${index}-${j}`}>
                      <div className="flex items-center gap-1">
                        <Label className="text-[10px] text-muted-foreground">Selling ₹</Label>
                        <Input type="number" value={it.price != null && it.price !== '' ? it.price : (it.list_price ?? '')}
                          onChange={(e) => {
                            const price = e.target.value === '' ? '' : Number(e.target.value);
                            const patch = { price };
                            if (it.discount_mode === 'PCT' && it.discount_pct) {
                              patch.discount = Math.round(Number(price || 0) * Number(it.discount_pct)) / 100;
                            }
                            onEditItem(index, j, patch);
                          }}
                          data-testid={`wa-item-price-${index}-${j}`} className="h-8 w-24 rounded-lg text-sm" />
                      </div>
                      <div className="flex items-center gap-1">
                        <Label className="text-[10px] text-muted-foreground">Discount</Label>
                        <div className="inline-flex overflow-hidden rounded-md border border-border" data-testid={`wa-item-disc-mode-${index}-${j}`}>
                          <button type="button" onClick={() => onEditItem(index, j, { discount_mode: 'AMT', discount: it.discount || 0 })}
                            data-testid={`wa-item-disc-amt-${index}-${j}`}
                            className={`px-1.5 py-1 text-[10px] font-semibold ${(it.discount_mode || 'AMT') === 'AMT' ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground'}`}>₹</button>
                          <button type="button" onClick={() => {
                              const pct = basePrice(it) > 0 ? Math.round((Number(it.discount || 0) / basePrice(it)) * 1000) / 10 : 0;
                              onEditItem(index, j, { discount_mode: 'PCT', discount_pct: pct });
                            }}
                            data-testid={`wa-item-disc-pct-${index}-${j}`}
                            className={`px-1.5 py-1 text-[10px] font-semibold ${it.discount_mode === 'PCT' ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground'}`}>%</button>
                        </div>
                        {it.discount_mode === 'PCT' ? (
                          <Input type="number" value={it.discount_pct ?? ''} placeholder="%"
                            onChange={(e) => {
                              const pct = e.target.value === '' ? 0 : Number(e.target.value);
                              onEditItem(index, j, { discount_pct: pct, discount: Math.round(basePrice(it) * pct) / 100 });
                            }}
                            data-testid={`wa-item-discount-${index}-${j}`} className="h-8 w-16 rounded-lg text-sm" />
                        ) : (
                          <Input type="number" value={it.discount || ''} placeholder="₹"
                            onChange={(e) => onEditItem(index, j, { discount: e.target.value === '' ? 0 : Number(e.target.value) })}
                            data-testid={`wa-item-discount-${index}-${j}`} className="h-8 w-20 rounded-lg text-sm" />
                        )}
                      </div>
                      <span className="ml-auto text-xs font-semibold" data-testid={`wa-item-final-${index}-${j}`}>
                        Final {money(lineAmount(it))}
                      </span>
                    </div>
                  )}
                  {(it.suggestions && it.suggestions.length > 0 &&
                    (it.resolution === 'SOLD' || it.resolution === 'UNKNOWN_CODE')) && (
                    <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2"
                      data-testid={`wa-suggest-${index}-${j}`}>
                      <p className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                        <Sparkles className="h-3.5 w-3.5" />
                        {it.resolution === 'SOLD'
                          ? `${it.barcode} is sold`
                          : `${it.barcode} is not in stock`}
                        {it.suggestion_confidence === 'strong'
                          ? ' — closest available match:'
                          : ' — nearest available matches:'}
                      </p>
                      <div className="mt-1.5 space-y-1.5">
                        {it.suggestions.map((s) => (
                          <div key={s.barcode}
                            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
                            data-testid={`wa-suggest-opt-${index}-${j}-${s.barcode}`}>
                            <span className="font-mono font-semibold">{s.barcode}</span>
                            {s.product ? <span className="max-w-[140px] truncate text-muted-foreground">{s.product}</span> : null}
                            <span className="text-muted-foreground">
                              · {s.store_name}{s.in_billing_store ? '' : ' · auto-transfer'}
                            </span>
                            <span className="text-muted-foreground">· {money(s.price)}</span>
                            <Button size="sm" variant="secondary"
                              className="ml-auto h-7 rounded-lg text-[11px] font-semibold"
                              data-testid={`wa-use-suggestion-${index}-${j}-${s.barcode}`}
                              onClick={() => onAcceptSuggestion(index, j, s.barcode)}>
                              Use {s.barcode}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {showEdit && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid={`wa-add-item-row-${index}`}>
              <Button size="sm" variant="outline" data-testid={`wa-add-item-${index}`}
                onClick={() => onAddItem(index)} className="h-8 rounded-lg text-xs">
                <PackageSearch className="mr-1.5 h-4 w-4" />Add item
              </Button>
              <Button size="sm" variant="outline" data-testid={`wa-add-unlisted-${index}`}
                onClick={() => onAddUnlisted(index)} className="h-8 rounded-lg text-xs">
                <Sparkles className="mr-1.5 h-4 w-4" />Add unlisted (AUTO-WA)
              </Button>
            </div>
          )}
        </div>

        {/* Groups (shorthand explain) */}
        {(d.groups || []).some((g) => g.count_expected != null) && (
          <div className="rounded-xl bg-muted/40 p-2.5" data-testid={`wa-groups-${index}`}>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Shorthand groups</p>
            {d.groups.filter((g) => g.count_expected != null).map((g) => (
              <p key={g.id} className="font-mono text-[11px]" data-testid={`wa-group-${index}-${g.id}`}>
                {g.family || '—'}: expected {g.count_expected} / detected {g.count_detected} ·{' '}
                <span className={g.validation === 'PASS' ? 'text-emerald-600' : 'text-rose-600'}>{g.validation}</span>
              </p>
            ))}
          </div>
        )}

        <Separator />

        {/* Payments */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Payments</p>
            <Button size="sm" variant="ghost" data-testid={`wa-add-payment-${index}`} onClick={() => onAddPayment(index)}
              className="h-7 rounded-lg text-xs text-primary">+ Payment</Button>
          </div>
          <div className="space-y-2">
            {(d.payments || []).map((p, j) => (
              <div key={j} className="flex items-center gap-2" data-testid={`wa-pay-${index}-${j}`}>
                <Select value={p.method || 'CASH'} onValueChange={(v) => { onEditPayment(index, j, { method: v }); }}>
                  <SelectTrigger className="h-9 w-24 rounded-lg" data-testid={`wa-pay-method-${index}-${j}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m === 'UNSPECIFIED' ? 'To review' : m}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={p.account || ''} onChange={(e) => onEditPayment(index, j, { account: e.target.value })}
                  onBlur={() => onRevalidate(index, d)} placeholder="Account"
                  data-testid={`wa-pay-account-${index}-${j}`} className="h-9 w-28 rounded-lg text-sm" />
                <Input value={p.amount == null ? '' : p.amount} onChange={(e) => onEditPayment(index, j, { amount: e.target.value === '' ? '' : Number(e.target.value) })}
                  onBlur={() => onRevalidate(index, d)} placeholder="Amount" type="number"
                  data-testid={`wa-pay-amount-${index}-${j}`} className="h-9 w-24 rounded-lg text-sm ml-auto" />
                <button onClick={() => onRemovePayment(index, j)} data-testid={`wa-pay-remove-${index}-${j}`}
                  className="text-muted-foreground hover:text-rose-500" aria-label="Remove payment">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          {/* Remember an unrecognised payment-account shorthand -> learned alias */}
          {canManageAliases && (d.payments || []).map((p, j) => {
            const alias = (p.account_alias || '').trim();
            const acct = (p.account || '').trim();
            if (!alias || !acct || alias.toLowerCase() === acct.toLowerCase()) return null;
            return (
              <button key={`rem-${j}`} type="button" data-testid={`wa-remember-acct-${index}-${j}`}
                onClick={() => onSaveAcctAlias(alias, acct)}
                className="mt-1 inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[11px] font-medium hover:bg-muted">
                <Sparkles className="h-3 w-3" />Remember “{alias}” → {acct}
              </button>
            );
          })}
          <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground" data-testid={`wa-pay-summary-${index}`}>
            <span>Paid {money(payTotal)}</span>
            <span className={balanceDue > 0.5 ? 'font-semibold text-amber-600' : ''}>Balance {money(balanceDue)}</span>
          </div>
        </div>

        {/* Salesperson */}
        <div className="flex items-center gap-2">
          <Label className="shrink-0 text-sm">Salesperson</Label>
          <div className="ml-auto w-44">
            <Select value={d.salesperson || ''} onValueChange={(v) => { onPatch(index, { salesperson: v }); onRevalidate(index, { ...d, salesperson: v }); }}>
              <SelectTrigger className={`h-9 rounded-lg ${!d.salesperson ? 'border-rose-400' : ''}`} data-testid={`wa-salesperson-${index}`}>
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {staff.map((u) => <SelectItem key={u.name} value={u.name} data-testid={`wa-sp-opt-${index}-${u.name}`}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {d.salesperson_raw && d.salesperson_status === 'ALIAS' && (
          <p className="text-[11px] text-emerald-600" data-testid={`wa-sp-alias-${index}`}>
            “{d.salesperson_raw}” matched to {d.salesperson} via a saved alias.
          </p>
        )}
        {d.salesperson_raw && !['RESOLVED', 'ALIAS'].includes(d.salesperson_status) && (
          <div className="flex flex-wrap items-center gap-2" data-testid={`wa-sp-hint-${index}`}>
            <p className="text-[11px] text-amber-600">
              Message said “{d.salesperson_raw}” — not a known staff member. Please pick one.
            </p>
            {canManageAliases && d.salesperson && (
              <button type="button" data-testid={`wa-save-alias-${index}`}
                onClick={() => onSaveAlias(d.salesperson_raw, d.salesperson)}
                className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium hover:bg-muted">
                Remember “{d.salesperson_raw}” → {d.salesperson}
              </button>
            )}
          </div>
        )}

        {/* Flags */}
        {blockFlags.length > 0 && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3" data-testid={`wa-block-flags-${index}`}>
            {blockFlags.map((f, k) => (
              <p key={k} className="flex items-start gap-1.5 text-xs text-rose-600"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{f.message}</p>
            ))}
          </div>
        )}
        {warnFlags.length > 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3" data-testid={`wa-warn-flags-${index}`}>
            {warnFlags.map((f, k) => (
              <p key={k} className="flex items-start gap-1.5 text-xs text-amber-700"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />{f.message}</p>
            ))}
          </div>
        )}

        {/* Edit sale panel (invoice-level; never touches master prices) */}
        {canEditPrice && (
          <div data-testid={`wa-edit-sale-${index}`}>
            <button type="button" data-testid={`wa-edit-sale-toggle-${index}`} onClick={() => setShowEdit((s) => !s)}
              className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showEdit ? 'rotate-90' : ''}`} />
              {showEdit ? 'Hide edit sale' : 'Edit sale (prices, discount, total)'}
            </button>
            {showEdit && (
              <div className={`mt-2 rounded-xl border border-border bg-muted/30 p-3 ${sourceImage ? 'lg:grid lg:grid-cols-2 lg:gap-4' : ''}`} data-testid={`wa-edit-panel-${index}`}>
                <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Items total{unpricedCount > 0 ? ` (+${unpricedCount} auto-priced)` : ''}</span>
                  <span className="font-medium" data-testid={`wa-items-subtotal-${index}`}>{money(pricedSubtotal)}</span>
                </div>
                {/* Bill discount: flat ₹ or % of the items total (one at a time) */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Bill discount</span>
                  <div className="flex items-center gap-1">
                    <div className="inline-flex overflow-hidden rounded-md border border-border" data-testid={`wa-bill-disc-mode-${index}`}>
                      <button type="button" data-testid={`wa-bill-disc-amt-${index}`} onClick={() => setBillMode('AMT')}
                        className={`px-1.5 py-1 text-[10px] font-semibold ${billMode === 'AMT' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>₹</button>
                      <button type="button" data-testid={`wa-bill-disc-pct-${index}`} onClick={() => setBillMode('PCT')}
                        className={`px-1.5 py-1 text-[10px] font-semibold ${billMode === 'PCT' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>%</button>
                    </div>
                    <Input type="number" value={billInput} placeholder={billMode === 'PCT' ? '%' : '₹'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setBillInput(v);
                        const num = v === '' ? 0 : Number(v);
                        const flat = billMode === 'PCT' ? Math.round(pricedSubtotal * num) / 100 : num;
                        onSetFinalTotal(index, Math.max(0, Math.round((pricedSubtotal - flat) * 100) / 100));
                      }}
                      onBlur={() => onRevalidate(index, d)}
                      data-testid={`wa-bill-discount-input-${index}`} className="h-8 w-20 rounded-lg text-sm" />
                  </div>
                </div>
                {billDiscount > 0 && (
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground" data-testid={`wa-bill-discount-${index}`}>
                    <span>Applied discount</span><span>- {money(billDiscount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <Label className="font-semibold">Final invoice total</Label>
                  <Input type="number" value={d.total == null ? '' : d.total}
                    onChange={(e) => { onSetFinalTotal(index, e.target.value); setBillInput(''); }}
                    onBlur={() => onRevalidate(index, d)}
                    data-testid={`wa-final-total-${index}`} className="h-9 w-28 rounded-lg text-right text-sm font-semibold" />
                </div>
                {d._original_total != null && Math.abs(Number(d._original_total) - finalTotal) > 0.5 && (
                  <p className="text-[11px] text-amber-600" data-testid={`wa-total-adjust-${index}`}>
                    Original WhatsApp total {money(d._original_total)} · adjustment {money(finalTotal - Number(d._original_total))}
                  </p>
                )}
                {balanceDue > 0.5 && (
                  <p className="text-[11px] text-amber-600">₹{balanceDue.toFixed(0)} will be recorded as an outstanding balance to reconcile.</p>
                )}
                </div>
                {sourceImage && (
                  <div className="mt-3 lg:mt-0" data-testid={`wa-edit-source-${index}`}>
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Source screenshot</span>
                      <button type="button" onClick={() => setShowSource((s) => !s)} data-testid={`wa-edit-source-toggle-${index}`}
                        className="text-[11px] font-medium text-primary hover:underline">{showSource ? 'Hide' : 'Show'}</button>
                    </div>
                    {showSource && (
                      <div className="relative max-h-72 overflow-auto rounded-lg border border-border bg-background">
                        <img src={sourceImage} alt="Source screenshot" data-testid={`wa-edit-source-img-${index}`}
                          className="w-full" style={{ display: 'block' }} />
                        {onViewSource && (
                          <button type="button" onClick={onViewSource} data-testid={`wa-edit-source-open-${index}`}
                            className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-background/90 px-2 py-1 text-[10px] font-medium shadow hover:bg-background">
                            <ZoomIn className="h-3 w-3" />Open
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer: net + actions */}
        <div className="flex items-center justify-between gap-2 pt-1">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Invoice total</p>
            <p className="font-display text-xl font-bold" data-testid={`wa-net-${index}`}>{money(d.total)}</p>
            {billDiscount > 0 && (
              <p className="text-[11px] text-muted-foreground">incl. {money(billDiscount)} bill discount</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" data-testid={`wa-exclude-${index}`} onClick={() => onPatch(index, { _state: 'skipped' })}
              className="h-10 rounded-xl text-muted-foreground">Skip</Button>
            <Button data-testid={`wa-confirm-${index}`} disabled={!canConfirm || busy}
              onClick={() => onCommit(index, false)} className="h-11 rounded-xl font-semibold">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              {busy ? 'Confirming…' : 'Confirm sale'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Original-screenshot lightbox: zoom (in/out/reset), drag-to-pan, native scroll
// for long screenshots, and switching between multiple uploaded screenshots.
// Opening it NEVER re-runs OCR — it only displays the already-uploaded image.
function ScreenshotLightbox({ images, index, onIndex, onClose }) {
  const open = index != null && Array.isArray(images) && images[index] != null;
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef(null);
  const drag = useRef({ on: false, x: 0, y: 0, sl: 0, st: 0 });

  React.useEffect(() => { setZoom(1); }, [index]);

  const total = (images || []).length;
  const go = (delta) => {
    if (total < 2) return;
    const next = ((index + delta) % total + total) % total;
    onIndex(next);
  };

  const onDown = (e) => {
    const el = scrollRef.current; if (!el) return;
    drag.current = { on: true, x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
  };
  const onMove = (e) => {
    const el = scrollRef.current; if (!el || !drag.current.on) return;
    el.scrollLeft = drag.current.sl - (e.clientX - drag.current.x);
    el.scrollTop = drag.current.st - (e.clientY - drag.current.y);
  };
  const endDrag = () => { drag.current.on = false; };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wa-screenshot-modal" className="max-w-3xl p-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ImageIcon className="h-4 w-4 text-primary" />
            {total > 1 ? `Screenshot ${index + 1} of ${total}` : 'Original screenshot'}
          </DialogTitle>
          <DialogDescription className="sr-only">Original uploaded WhatsApp screenshot</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1.5 px-4 pb-2">
          <Button size="sm" variant="outline" data-testid="wa-shot-zoom-out" className="h-8 w-8 rounded-lg p-0"
            onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.5) * 10) / 10))} disabled={zoom <= 1}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="w-12 text-center text-xs tabular-nums text-muted-foreground" data-testid="wa-shot-zoom-level">{Math.round(zoom * 100)}%</span>
          <Button size="sm" variant="outline" data-testid="wa-shot-zoom-in" className="h-8 w-8 rounded-lg p-0"
            onClick={() => setZoom((z) => Math.min(4, Math.round((z + 0.5) * 10) / 10))} disabled={zoom >= 4}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" data-testid="wa-shot-zoom-reset" className="h-8 rounded-lg text-xs" onClick={() => setZoom(1)}>
            Reset
          </Button>
          {total > 1 && (
            <div className="ml-auto flex items-center gap-1.5">
              <Button size="sm" variant="outline" data-testid="wa-shot-prev" className="h-8 w-8 rounded-lg p-0" onClick={() => go(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" data-testid="wa-shot-next" className="h-8 w-8 rounded-lg p-0" onClick={() => go(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <div
          ref={scrollRef}
          data-testid="wa-shot-viewport"
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          className="max-h-[70vh] overflow-auto bg-muted/40"
          style={{ cursor: zoom > 1 ? 'grab' : 'default' }}
        >
          {open && (
            <img
              src={images[index].b64}
              alt={images[index].name || `Screenshot ${index + 1}`}
              data-testid="wa-shot-image"
              draggable={false}
              style={{ width: `${zoom * 100}%`, maxWidth: 'none', display: 'block', userSelect: 'none' }}
            />
          )}
        </div>
        {total > 1 && (
          <div className="flex flex-wrap gap-1.5 px-4 py-3" data-testid="wa-shot-strip">
            {images.map((im, i) => (
              <button key={i} type="button" data-testid={`wa-shot-thumb-${i}`} onClick={() => onIndex(i)}
                className={`h-12 w-12 overflow-hidden rounded-md border-2 transition-colors ${i === index ? 'border-primary' : 'border-border hover:border-primary/50'}`}>
                <img src={im.b64} alt={im.name || `Screenshot ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Add an existing garment by searching BOTH stores. Billing-store AVAILABLE items
// are listed first, then other-store AVAILABLE (which ride the existing auto-transfer
// on confirm). Searching/selecting never mutates stock.
function AddItemDialog({ open, storeId, storeName, onClose, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (!open) { setQ(''); setRows([]); } }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setRows([]); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/garments', { params: { q: term, status: 'AVAILABLE', limit: 40 } });
        if (!alive) return;
        const list = r.data.data || [];
        list.sort((a, b) => (a.store_id === storeId ? 0 : 1) - (b.store_id === storeId ? 0 : 1));
        setRows(list);
      } catch (e) { if (alive) toast.error(errMsg(e)); }
      finally { if (alive) setLoading(false); }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open, storeId]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wa-additem-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><PackageSearch className="h-4 w-4 text-primary" />Add item</DialogTitle>
          <DialogDescription>Search available stock across all stores. Other-store items auto-transfer on confirm.</DialogDescription>
        </DialogHeader>
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by code, product or colour…"
          data-testid="wa-additem-search" className="h-10 rounded-lg" />
        <div className="max-h-[50vh] space-y-1.5 overflow-auto">
          {loading && <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Searching…</div>}
          {!loading && q.trim().length >= 2 && rows.length === 0 && (
            <p className="py-4 text-sm text-muted-foreground" data-testid="wa-additem-empty">No available items match “{q}”. Use “Add unlisted” instead.</p>
          )}
          {rows.map((g) => {
            const here = g.store_id === storeId;
            return (
              <button key={g.barcode} type="button" data-testid={`wa-additem-opt-${g.barcode}`} onClick={() => onPick(g)}
                className="flex w-full items-center gap-2 rounded-lg border border-border p-2 text-left hover:bg-muted">
                <span className="font-mono text-sm font-semibold">{g.barcode}</span>
                {g.product ? <span className="max-w-[150px] truncate text-xs text-muted-foreground">{g.product}</span> : null}
                <span className="ml-auto text-xs text-muted-foreground">{money(g.price)}</span>
                <Badge variant="outline" className={`text-[10px] ${here ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' : 'bg-sky-500/15 text-sky-600 border-sky-500/30'}`}>
                  {here ? (storeName || 'This store') : 'Other store · transfer'}
                </Badge>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Add an off-tag / unlisted item -> unique AUTO-WA record created only on confirm.
function AddUnlistedDialog({ open, onClose, onAdd }) {
  const [desc, setDesc] = useState('');
  const [price, setPrice] = useState('');
  React.useEffect(() => { if (!open) { setDesc(''); setPrice(''); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wa-unlisted-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Sparkles className="h-4 w-4 text-primary" />Add unlisted item</DialogTitle>
          <DialogDescription>A unique AUTO-WA record is created only when the sale is confirmed. It will appear in Inventory Review.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Description / category</Label>
            <Input autoFocus value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. KB Velvet"
              data-testid="wa-unlisted-desc" className="mt-1 h-10 rounded-lg" />
          </div>
          <div>
            <Label className="text-xs">Selling amount (optional)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="₹"
              data-testid="wa-unlisted-price" className="mt-1 h-10 rounded-lg" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="wa-unlisted-cancel">Cancel</Button>
          <Button disabled={!desc.trim()} data-testid="wa-unlisted-add"
            onClick={() => onAdd(desc, price === '' ? null : Number(price))}>Add item</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


